import type { AuthenticatedUser } from "@dosa/types";
import type { RequestOtpInput, VerifyOtpInput } from "@dosa/validation";

/**
 * Real calls to the Worker's Authentica OTP login routes (apps/worker/src/auth/routes.ts).
 * The browser never talks to Authentica directly and never sees an API key — it only ever
 * calls DOSA's own /api/auth/otp/* endpoints, which hold the Authentica secret server-side.
 * Session state comes back as an HttpOnly cookie the browser can't read; `fetchMe` is how the
 * app learns who's logged in after a refresh.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

interface ApiErrorBody {
  error?: { code: string; message: string };
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const message = (body as ApiErrorBody | null)?.error?.message ?? `Request failed (${res.status})`;
    throw new Error(message);
  }
  return body as T;
}

export async function requestOtp(input: RequestOtpInput): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/auth/otp/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  await parseJsonOrThrow(res);
}

export async function verifyOtpAndLogin(input: VerifyOtpInput): Promise<AuthenticatedUser> {
  const res = await fetch(`${API_BASE_URL}/api/auth/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  return parseJsonOrThrow<AuthenticatedUser>(res);
}

export async function fetchMe(): Promise<AuthenticatedUser | null> {
  const res = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: "include" });
  if (res.status === 401) return null;
  return parseJsonOrThrow<AuthenticatedUser>(res);
}

export async function realLogout(): Promise<void> {
  await fetch(`${API_BASE_URL}/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => undefined);
}
